import React, { FC } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { Container } from '@fiverr-private/layout_components';
import { Values } from '@fiverr-private/dropdown';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate as t } from '@fiverr-private/i18n-react';
import { formatDate } from '@fiverr-private/futile';
import { Option, Select } from '@fiverr-private/forms';
import { Text } from '@fiverr-private/typography';
import { buildDate, T_FORM_KEY } from '../../../utils';
import { DatesFieldProps, WorkExperienceFormType } from '../../../shared/types';
import { months } from '../defaults';

interface MonthsProps extends DatesFieldProps {
    isStart?: boolean;
}

const Months: FC<MonthsProps> = ({
    isStart,
    errors,
    setError,
    setValue,
    getValues,
    trigger,
    clearErrors,
    control,
    currentlyWorking,
}) => {
    const { locale } = getContext();

    const handleMonthChange = (
        value: Values,
        onChange: ControllerRenderProps<WorkExperienceFormType>['onChange'],
        isStart?: boolean
    ) => {
        if (!value || !value.item) {
            return;
        }

        const updatedMonth = value.item as string;
        const startYear = getValues('startYear');
        const endYear = getValues('endYear');

        onChange(updatedMonth);
        if (isStart && startYear) {
            setValue('startDate', buildDate(startYear, updatedMonth));
        } else if (endYear) {
            setValue('endDate', buildDate(endYear, updatedMonth));
        }
        trigger(isStart ? 'startMonth' : 'endMonth');
    };

    const getMonthsErrorMessageWidth = (isStart?: boolean) =>
        errors?.[isStart ? 'startMonth' : 'endMonth']?.type === 'validate' ? '200%' : '100%';

    const validateMonth = (formValues: WorkExperienceFormType, isStart?: boolean) => {
        const relevantDate = formValues[isStart ? 'startDate' : 'endDate'];
        if (relevantDate && relevantDate.valueOf() > new Date().valueOf()) {
            const errorMessage = t(`${T_FORM_KEY}.fields.dates.future_${isStart ? 'start' : 'end'}_date_error`);
            setError(isStart ? 'startYear' : 'endYear', {
                type: 'manual',
                message: errorMessage,
            });
            return errorMessage;
        }

        if (formValues.startDate && formValues.endDate) {
            if (formValues.startDate > formValues.endDate) {
                const errorMessage = t(`${T_FORM_KEY}.fields.dates.end_date_before_start_date_error`);
                setError('endYear', {
                    type: 'manual',
                    message: errorMessage,
                });

                if (!isStart) {
                    return errorMessage;
                }

                setError('endMonth', {
                    type: 'validate',
                    message: errorMessage,
                });
            } else {
                clearErrors('endYear');
                clearErrors('endMonth');
            }
        }

        const currentYearError = errors?.[isStart ? 'startYear' : 'endYear'];
        if (currentYearError?.type === 'manual') {
            clearErrors(isStart ? 'startYear' : 'endYear');
        }

        return true;
    };

    return (
        <Container marginRight="6" flex={1} maxWidth="50%" data-testid={isStart ? 'start-month' : 'end-month'}>
            <Controller
                name={isStart ? 'startMonth' : 'endMonth'}
                control={control}
                rules={{
                    required:
                        isStart || !currentlyWorking
                            ? t(`${T_FORM_KEY}.fields.dates.${isStart ? 'start' : 'end'}_month_required_error`)
                            : false,
                    deps: isStart ? ['startYear', 'startDate'] : ['endYear', 'endDate'],
                    validate: (_, formValues) => validateMonth(formValues, isStart),
                }}
                render={({ field: { onChange, value } }) => (
                    <Select
                        disabled={!isStart && currentlyWorking}
                        fullWidth
                        size="lg"
                        values={{ item: value }}
                        placeholder={t(`${T_FORM_KEY}.fields.dates.placeholder_month`)}
                        isError={isStart ? !!errors?.startMonth : !!errors?.endMonth}
                        onChange={(value) => handleMonthChange(value, onChange, isStart)}
                    >
                        {months.map((monthDate, index) => (
                            <Option value={index.toString()} key={index}>
                                {formatDate(monthDate, { locale, year: false, day: false, month: 'long' })}
                            </Option>
                        ))}
                    </Select>
                )}
            />
            <Text
                color="validationError"
                marginTop="2"
                hidden={isStart ? !errors?.startMonth : !errors?.endMonth || currentlyWorking}
                width={getMonthsErrorMessageWidth(isStart)}
            >
                {isStart ? errors?.startMonth?.message : errors?.endMonth?.message}
            </Text>
        </Container>
    );
};

export default Months;
